// The CLI grammar, as a pure function. No DOM, no api, no params: the whole
// point is that the language -- and especially the error text, which is the
// teaching material -- can be unit tested without a browser or a speaker.
//
// GRAMMAR DECISIONS (the spec leaves these open; here is what was chosen)
//
//   loop <sound> <pattern>
//     The named sound is the *default track*: bare `x` symbols land there.
//     A symbol that is itself a sound name switches that one cell to that
//     sound's track, so the spec's own example -- `loop kick _ _ kick _
//     snare _ _` -- does the obvious thing (kick on 1 and 4, snare on 6)
//     instead of being a type error. Only tracks actually mentioned are
//     written; everything else is left alone. `loop` replaces a track
//     rather than adding to it, so `loop kick ................` clears it.
//
//   Where the track name stops and the pattern starts
//     The spec writes both `loop snare x...x...x...x...`, where the name is
//     outside the pattern, and `loop kick _ _ kick _ snare _ _`, which it
//     calls eight symbols -- so there the name is also the first symbol.
//     Both are honoured: the name-outside reading is tried first, and the
//     name-as-first-hit reading is the fallback. That is unambiguous
//     because the two readings differ by one symbol and no two legal
//     lengths are adjacent.
//
//   Pattern length
//     16 symbols is a bar. 8 symbols is a half-bar, repeated twice; 4
//     symbols is a one-beat cell, repeated four times. So `loop kick _ _
//     kick` is a four-symbol cell -- kick on the beat and on the last
//     sixteenth of it -- giving steps 1, 4, 5, 8, 9, 12, 13, 16. Any other
//     length is an error, because silently truncating or padding a
//     5-symbol pattern is how you lose an hour.
//
//   Two notations, one rule
//     Symbols are whitespace separated, *unless* every token is made only
//     of step characters and at least one is longer than one character --
//     then the tokens are concatenated and read per character. That makes
//     `x...x...x...x...` and `x... x... x... x...` and `x . . . x ...`
//     the same pattern, while keeping sound names (which contain letters
//     other than `x`) unambiguous.
//
//   Case and whitespace are not meaningful. On: `x`. Off: `.` `_` `-`.
//
// Every failure returns the same shape: `error` is the full sentence the
// spec asks for, `terse` is the one-liner for terse verbosity, `usage` is
// the hint added at helpful verbosity, and `suggestion` is the nearest
// match by edit distance (never a hardcoded pair). With `hiddenLabels` the
// round is a recall task, so nothing here may name a sound the person did
// not already type.

export const COMMANDS = ['play', 'loop', 'tempo', 'clear', 'start', 'stop', 'help']

export const USAGE = {
  play: 'play <sound>',
  loop: 'loop <sound> <16, 8 or 4 symbols>',
  tempo: 'tempo <40-240>',
  clear: 'clear',
  start: 'start',
  stop: 'stop',
  help: 'help [command]',
}

export const STEPS = 16
export const TEMPO_MIN = 40
export const TEMPO_MAX = 240

const OFF = new Set(['.', '_', '-'])
const ON = 'x'
const STEP_TOKEN = /^[x._-]+$/
const LENGTHS = { 16: 1, 8: 2, 4: 4 }

// Damerau-Levenshtein (optimal string alignment). The transposition case is
// the reason not to use plain Levenshtein here: 'hta' for 'hat' is one of the
// commonest real typos and plain edit distance charges it two.
export function editDistance(a, b) {
  if (a === b) return 0
  const rows = []
  for (let i = 0; i <= a.length; i++) rows.push(new Array(b.length + 1).fill(0))
  for (let i = 0; i <= a.length; i++) rows[i][0] = i
  for (let j = 0; j <= b.length; j++) rows[0][j] = j
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      let d = Math.min(rows[i][j - 1] + 1, rows[i - 1][j] + 1, rows[i - 1][j - 1] + cost)
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d = Math.min(d, rows[i - 2][j - 2] + 1)
      }
      rows[i][j] = d
    }
  }
  return rows[a.length][b.length]
}

// Nearest candidate, but only when it is near enough to be worth saying: a
// wrong suggestion is worse than none, because the person will type it.
export function nearest(word, candidates) {
  let best = null
  let bestD = Infinity
  for (const c of candidates) {
    const d = editDistance(word, c)
    if (d < bestD) { best = c; bestD = d }
  }
  if (best === null) return null
  const limit = Math.min(3, Math.max(2, Math.floor(Math.max(word.length, best.length) / 3)))
  if (bestD > limit && !best.startsWith(word) && !word.startsWith(best)) return null
  return best
}

function fail(parts) {
  return { ok: false, suggestion: null, usage: null, ...parts }
}

// The "no sound called 'kik'. Try: kick snare hat tone" message from the
// spec, minus the sound list when the labels are hidden.
function unknownSound(word, sounds, hiddenLabels) {
  if (hiddenLabels) {
    return fail({
      error: `no sound called '${word}'.`,
      terse: `? '${word}'`,
      usage: 'four sounds are loaded; their names are hidden this round',
    })
  }
  const suggestion = nearest(word, sounds)
  return fail({
    error: `no sound called '${word}'. Try: ${sounds.join(' ')}`,
    terse: suggestion ? `? '${word}' -> ${suggestion}` : `? '${word}'`,
    suggestion,
    usage: suggestion ? `did you mean '${suggestion}'?` : null,
  })
}

function unknownCommand(word) {
  const suggestion = nearest(word, COMMANDS)
  return fail({
    error: `no command called '${word}'. Try: ${COMMANDS.join(' ')}`,
    terse: suggestion ? `? '${word}' -> ${suggestion}` : `? '${word}'`,
    suggestion,
    usage: suggestion ? `did you mean '${suggestion}'?` : 'help lists every command',
  })
}

export function parse(line, { sounds = [], hiddenLabels = false } = {}) {
  const tokens = String(line ?? '').trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (tokens.length === 0) {
    return fail({ empty: true, error: 'type a command, or `help`.', terse: '?' })
  }

  const [name, ...args] = tokens
  if (!COMMANDS.includes(name)) return unknownCommand(name)

  const noArgs = (command) =>
    args.length === 0
      ? { ok: true, command }
      : fail({
          error: `${command} takes no arguments (got '${args.join(' ')}').`,
          terse: `? ${command}`,
          usage: USAGE[command],
        })

  switch (name) {
    case 'play': {
      if (args.length === 0) {
        return fail({
          error: 'play needs a sound.',
          terse: '? play',
          usage: USAGE.play,
        })
      }
      if (args.length > 1) {
        return fail({
          error: `play takes one sound (got ${args.length}).`,
          terse: '? play',
          usage: USAGE.play,
        })
      }
      if (!sounds.includes(args[0])) return unknownSound(args[0], sounds, hiddenLabels)
      return { ok: true, command: 'play', sound: args[0] }
    }

    case 'loop':
      return parseLoop(args, sounds, hiddenLabels)

    case 'tempo': {
      if (args.length === 0) {
        return fail({ error: 'tempo needs a number.', terse: '? tempo', usage: USAGE.tempo })
      }
      if (args.length > 1) {
        return fail({
          error: `tempo takes one number (got '${args.join(' ')}').`,
          terse: '? tempo',
          usage: USAGE.tempo,
        })
      }
      const bpm = Number(args[0])
      if (!Number.isFinite(bpm)) {
        return fail({
          error: `'${args[0]}' is not a number.`,
          terse: `? '${args[0]}'`,
          usage: USAGE.tempo,
        })
      }
      if (bpm < TEMPO_MIN || bpm > TEMPO_MAX) {
        return fail({
          error: `tempo must be between ${TEMPO_MIN} and ${TEMPO_MAX} (got ${bpm}).`,
          terse: `? ${bpm}`,
          usage: USAGE.tempo,
        })
      }
      return { ok: true, command: 'tempo', bpm }
    }

    case 'help': {
      if (args.length === 0) return { ok: true, command: 'help', topic: null }
      if (args.length > 1) {
        return fail({ error: 'help takes one command.', terse: '? help', usage: USAGE.help })
      }
      if (!COMMANDS.includes(args[0])) return unknownCommand(args[0])
      return { ok: true, command: 'help', topic: args[0] }
    }

    default:
      return noArgs(name)
  }
}

// The compact notation: only run the tokens together when none of them could
// be a sound name, otherwise `loop kick x x` would become `xx`.
function symbolise(tokens) {
  const compact = tokens.every((t) => STEP_TOKEN.test(t)) && tokens.some((t) => t.length > 1)
  return compact ? tokens.join('').split('') : tokens
}

function parseLoop(args, sounds, hiddenLabels) {
  if (args.length === 0) {
    return fail({ error: 'loop needs a sound and a pattern.', terse: '? loop', usage: USAGE.loop })
  }
  const track = args[0]
  if (!sounds.includes(track)) return unknownSound(track, sounds, hiddenLabels)

  const rest = args.slice(1)
  if (rest.length === 0) {
    return fail({
      error: `loop ${track} needs a pattern, ${STEPS}, 8 or 4 symbols long.`,
      terse: '? loop',
      usage: USAGE.loop,
      suggestion: `loop ${track} x...x...x...x...`,
    })
  }

  // `loop snare x...x...` has the track name outside the pattern, while the
  // spec's `loop kick _ _ kick _ snare _ _` counts it as the first symbol.
  // Both readings can be tried because their lengths differ by one and no
  // two legal lengths are adjacent: prefer the name-outside reading, fall
  // back to the name-as-first-hit one.
  const tail = symbolise(rest)
  const withHead = symbolise([track, ...rest])
  const symbols = LENGTHS[tail.length] ? tail : withHead

  const repeat = LENGTHS[symbols.length]
  if (!repeat) {
    return fail({
      error: `a loop is ${STEPS}, 8 or 4 symbols (8 and 4 repeat to fill the bar); got ${tail.length}.`,
      terse: `? ${tail.length} symbols`,
      usage: USAGE.loop,
    })
  }

  const cells = symbols.length
  const tracks = { [track]: new Array(cells).fill(false) }
  for (let i = 0; i < cells; i++) {
    const sym = symbols[i]
    if (OFF.has(sym)) continue
    if (sym === ON) { tracks[track][i] = true; continue }
    if (!sounds.includes(sym)) return unknownSound(sym, sounds, hiddenLabels)
    tracks[sym] ??= new Array(cells).fill(false)
    tracks[sym][i] = true
  }

  const expand = (cell) => {
    const out = new Array(STEPS).fill(false)
    for (let r = 0; r < repeat; r++) {
      for (let i = 0; i < cells; i++) out[r * cells + i] = cell[i]
    }
    return out
  }

  const expanded = Object.fromEntries(Object.entries(tracks).map(([k, v]) => [k, expand(v)]))
  return { ok: true, command: 'loop', sound: track, steps: expanded[track], tracks: expanded }
}

// Help text lives here too, because what it may say depends on the same
// hidden-labels rule as the errors, and that rule should have one home.
export function helpLines({ sounds = [], hiddenLabels = false, verbosity = 'helpful' } = {}, topic = null) {
  if (topic) {
    const detail = {
      play: 'trigger a sound now',
      loop: 'set a track: x is a hit, . or _ is a rest; 8 or 4 symbols repeat',
      tempo: `beats per minute, ${TEMPO_MIN} to ${TEMPO_MAX}`,
      clear: 'empty every track',
      start: 'start the loop',
      stop: 'stop the loop',
      help: 'this list',
    }
    return verbosity === 'terse' ? [USAGE[topic]] : [USAGE[topic], detail[topic]]
  }

  if (verbosity === 'terse') return [COMMANDS.join(' ')]

  const lines = [
    'play <sound>                 trigger it now',
    'loop <sound> x...x...x...x...  set the track (16, 8 or 4 symbols)',
    'loop <sound> _ _ <sound> _ _ _ _   names work as symbols too',
    `tempo <${TEMPO_MIN}-${TEMPO_MAX}>`,
    'clear                        empty every track',
    'start / stop                 run the loop',
    'help [command]',
  ]
  lines.push(hiddenLabels ? 'sounds: hidden this round' : `sounds: ${sounds.join(' ')}`)
  return lines
}
